import type { ScanAnalytics, ScanAnalyticsPeriod, RecentScansPage } from "types/scanAnalytics";

function num(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function str(value: unknown, fallback = ""): string {
  return value == null ? fallback : String(value);
}

export function normalizeScanAnalytics(raw: unknown): ScanAnalytics {
  const data = (raw as any)?.data ?? raw ?? {};
  const period = str(data.period ?? data.Period, "daily").toLowerCase() as ScanAnalyticsPeriod;

  const mapItems = (items: unknown, labelKey: string, countKey: string) => {
    if (!Array.isArray(items)) return [];
    return items.map((item) => ({
      label: str((item as any)[labelKey] ?? (item as any).Label),
      count: num((item as any)[countKey] ?? (item as any).Count),
    }));
  };

  const mapPeriod = (items: unknown) => {
    if (!Array.isArray(items)) return [];
    return items.map((item) => ({
      label: str((item as any).label ?? (item as any).Label),
      key: str((item as any).key ?? (item as any).Key),
      count: num((item as any).count ?? (item as any).Count),
    }));
  };

  return {
    totalScans: num(data.totalScans ?? data.TotalScans),
    scansToday: num(data.scansToday ?? data.ScansToday),
    scansThisWeek: num(data.scansThisWeek ?? data.ScansThisWeek),
    scansThisMonth: num(data.scansThisMonth ?? data.ScansThisMonth),
    weekRangeFrom: (data.weekRangeFrom ?? data.WeekRangeFrom ?? null) as string | null,
    weekRangeTo: (data.weekRangeTo ?? data.WeekRangeTo ?? null) as string | null,
    monthRangeFrom: (data.monthRangeFrom ?? data.MonthRangeFrom ?? null) as string | null,
    monthRangeTo: (data.monthRangeTo ?? data.MonthRangeTo ?? null) as string | null,
    lastScanAt: (data.lastScanAt ?? data.LastScanAt ?? null) as string | null,
    period: period === "weekly" || period === "monthly" ? period : "daily",
    periodBreakdown: mapPeriod(data.periodBreakdown ?? data.PeriodBreakdown),
    topLocations: mapItems(data.topLocations ?? data.TopLocations, "label", "count"),
    topCities: mapItems(data.topCities ?? data.TopCities, "label", "count"),
    topCountries: mapItems(data.topCountries ?? data.TopCountries, "label", "count"),
    topContinents: mapItems(data.topContinents ?? data.TopContinents, "label", "count"),
    topReferrers: mapItems(data.topReferrers ?? data.TopReferrers, "label", "count"),
    topDeviceTypes: mapItems(data.topDeviceTypes ?? data.TopDeviceTypes, "label", "count"),
  };
}

export function normalizeRecentScansPage(
  raw: unknown,
  page: number,
  pageSize: number
): RecentScansPage {
  const payload: any = (raw as any)?.data ?? raw ?? {};
  const items = payload.items ?? payload.Items ?? payload.data ?? [];
  const total = num(payload.totalCount ?? payload.TotalCount ?? payload.total ?? 0);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const mapRecent = (list: unknown) => {
    if (!Array.isArray(list)) return [];
    return list.map((item) => ({
      scannedAt: str((item as any).scannedAt ?? (item as any).ScannedAt),
      profileName: str(
        (item as any).profileName ?? (item as any).ProfileName,
        "Unknown profile"
      ),
      profileType: str(
        (item as any).profileType ?? (item as any).ProfileType,
        "Unknown"
      ),
      location: str((item as any).location ?? (item as any).Location, "Unknown"),
      referrerSource: str(
        (item as any).referrerSource ?? (item as any).ReferrerSource,
        "Unknown"
      ),
      deviceType: str((item as any).deviceType ?? (item as any).DeviceType, "Unknown"),
      operatingSystem: ((item as any).operatingSystem ?? (item as any).OperatingSystem ?? null) as
        | string
        | null,
    }));
  };

  return {
    data: mapRecent(items),
    pagination: {
      page,
      pageSize,
      total,
      totalPages,
    },
  };
}
